/**
 * GEN-3604 — o portal pode viver em subdomínio próprio (`autosservico.taxly.com.br`, `portal.taxly.com.br`)
 * servindo o MESMO build da plataforma: o host decide se o app monta o portal na raiz (URL limpa,
 * sem `/portal`) ou se ele fica em `/portal/*` dentro do host da plataforma.
 *
 * Infra: DNS do subdomínio → mesmo distribuidor/servidor do front; o CORS do backend já aceita
 * `https://*.taxly.com.br`. Para o login Microsoft funcionar no subdomínio, a URL de redirect do app
 * no Entra precisa incluir o novo host (`REACT_APP_MSAL_REDIRECT_URI` no build do portal).
 */

const PORTAL_HOST_PREFIXES = ["autosservico.", "auto-servico.", "autoservico.", "portal."];

// `REACT_APP_PORTAL_HOST` (definido no build via `vite.config.ts`) pode apontar para
// um hostname que não começa por nenhum dos 4 prefixos fixos acima — nesse caso `isPortalHost` sempre
// devolvia `false`, e o endereço que o painel do admin anuncia (`portalAddress`, em `PortalAdmin.tsx`)
// abriria a PLATAFORMA em vez do portal. Extrai só o hostname da env var (ela pode vir com protocolo,
// ex. `https://autosservico.taxly.com.br`) e compara também por ele.
const configuredPortalHostname = (): string | null => {
  const raw = (process.env.REACT_APP_PORTAL_HOST ?? "").trim();
  if (!raw) return null;
  try {
    return new URL(raw.includes("://") ? raw : `https://${raw}`).hostname.toLowerCase();
  } catch {
    return null;
  }
};

export function isPortalHost(
  hostname: string = typeof window !== "undefined" ? window.location.hostname : ""
): boolean {
  const h = (hostname ?? "").toLowerCase();
  if (!h) return false;
  if (h === configuredPortalHostname()) return true;
  return PORTAL_HOST_PREFIXES.some((p) => h.startsWith(p));
}

/** Prefixo das rotas do portal: vazio no subdomínio dedicado, `/portal` dentro da plataforma. */
export function portalBasePath(hostname?: string): string {
  return isPortalHost(hostname) ? "" : "/portal";
}

export const portalPath = (sub: "" | "/login" = "", hostname?: string): string => {
  const base = portalBasePath(hostname);
  const path = `${base}${sub}`;
  return path === "" ? "/" : path;
};

/**
 * Para onde o popup do Microsoft volta quando o login é feito DE DENTRO do portal.
 *
 * O `msalConfig` da plataforma aponta o `redirectUri` para `REACT_APP_MSAL_REDIRECT_URI` e, sem a env,
 * cai numa URL fixa de produção. No portal isso quebra de dois jeitos: no host próprio o redirect é
 * cross-origin (o popup nunca entrega o token); no `/portal/*` de um ambiente sem a env (local,
 * homologação) o popup volta para o host de produção. Por isso o portal calcula o destino a partir
 * do PRÓPRIO origin: `<origin>/login` no host dedicado, `<origin>/portal/login` na plataforma. A env,
 * quando definida, continua valendo como override — só no portal por caminho, porque no host próprio
 * ela aponta para a plataforma e seria justamente o redirect cross-origin que se quer evitar.
 *
 * ⚠️ Cada URI devolvida aqui precisa estar registrada no app do Entra (`https://<host>/login` e
 * `https://<host da plataforma>/portal/login`); sem isso o Entra recusa com AADSTS50011.
 */
export function portalMsalRedirectUri(
  hostname: string = typeof window !== "undefined" ? window.location.hostname : "",
  origin: string = typeof window !== "undefined" ? window.location.origin : "",
  envOverride: string | undefined = process.env.REACT_APP_MSAL_REDIRECT_URI
): string {
  if (isPortalHost(hostname)) return `${origin}${portalPath("/login", hostname)}`;
  const override = (envOverride ?? "").trim();
  return override || `${origin}${portalPath("/login", hostname)}`;
}
